import React, { useCallback, useEffect, useState } from 'react'

import { inject, observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import FileInputBase from '~/client/src/shared/components/FileInput/FileInput'
import { ChecklistIcon } from '~/client/src/shared/components/icons/ChecklistIcon'
import SitePermitCreationFormContent from '~/client/src/shared/components/SitePermitCreationForm/SitePermitCreationFormContent'
import SitePermitCreationFormStore from '~/client/src/shared/components/SitePermitCreationForm/WorkflowCreationForm.store'
import IWorkflowCreationParent from '~/client/src/shared/components/WorkflowDirectory/IWorkflowCreationParent'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import MaterialCategoriesStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import PermitInspectionChangesStore from '~/client/src/shared/stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '~/client/src/shared/stores/domain/PermitInspections.store'
import PermitStatusChangesStore from '~/client/src/shared/stores/domain/PermitStatusChanges.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import RecurringWorkflowSettingsStore from '~/client/src/shared/stores/domain/RecurringWorkflowSettings.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { applyParentFieldDefaults } from '~/client/src/shared/utils/linkedWorkflowFieldInheritance'

interface IProps {
  parentStore: IWorkflowCreationParent
  allowedTypeIds?: string[]
  isPreviewMode?: boolean
  allowSelectExisting?: boolean
  onBackToList: () => void
  onCancel?: () => void
  eventsStore?: EventsStore
  workflowTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  userProjectsStore?: UserProjectsStore
  projectDateStore?: ProjectDateStore
  fileUploadingStore?: FileUploadingStore
  permitStatusChangesStore?: PermitStatusChangesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoriesStore
  tagsStore?: TagsStore
  recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore
  graphExecutorStore?: GraphExecutorStore
  sitemapItemsStore?: SitemapItemsStore
  closuresStore?: ClosuresStore
  markupsStore?: MarkupsStore
  calendarPlaceholderStore?: CalendarPlaceholderStore
}

const WorkflowCreationPanel: React.FC<IProps> = observer(
  ({
    parentStore,
    allowedTypeIds,
    isPreviewMode = false,
    allowSelectExisting = true,
    onBackToList,
    onCancel,
    eventsStore,
    workflowTypesStore,
    companiesStore,
    userProjectsStore,
    projectDateStore,
    fileUploadingStore,
    permitStatusChangesStore,
    permitInspectionsStore,
    permitInspectionChangesStore,
    projectMembersStore,
    locationAttributesStore,
    materialsStore,
    materialCategoryStore,
    tagsStore,
    recurringWorkflowSettingsStore,
    graphExecutorStore,
    sitemapItemsStore,
    closuresStore,
    markupsStore,
    calendarPlaceholderStore,
  }) => {
    const [creationStore, setCreationStore] =
      useState<SitePermitCreationFormStore | null>(null)

    useEffect(() => {
      const newStore = new SitePermitCreationFormStore(
        eventsStore,
        projectDateStore,
        fileUploadingStore,
        workflowTypesStore,
        userProjectsStore,
        projectMembersStore,
        companiesStore,
        locationAttributesStore,
        materialsStore,
        materialCategoryStore,
        tagsStore,
        graphExecutorStore,
        closuresStore,
        markupsStore,
        null,
        permitStatusChangesStore,
        permitInspectionsStore,
        permitInspectionChangesStore,
        calendarPlaceholderStore,
        recurringWorkflowSettingsStore,
        sitemapItemsStore,
        parentStore.addToUploadLogMap?.bind(parentStore),
        parentStore.extendLinkedWorkflowsIds,
        inheritParentFields,
      )
      newStore.isNestedCreation = true

      function inheritParentFields() {
        applyParentFieldDefaults(
          newStore.editablePermit,
          parentStore.editablePermit,
          newStore.template?.initialStep?.fields || [],
        )
      }

      newStore.resetSelectedField()

      const templateId = allowedTypeIds?.length
        ? allowedTypeIds[0]
        : parentStore.template?.id
      newStore.setDefaultSitePermit(null, templateId, false)

      inheritParentFields()

      if (isPreviewMode) {
        newStore.setPreviewTemplate(null)
      }

      const shouldShowTypePicker =
        !allowedTypeIds?.length || allowedTypeIds.length > 1
      if (shouldShowTypePicker) {
        newStore.showFormTypeSelector()
      }

      setCreationStore(newStore)
    }, [])

    const handleApplyNewWorkflow = useCallback(() => {
      if (!creationStore || !parentStore) return

      const applySelection = () => {
        parentStore.changeSelectedFieldValue(creationStore.editablePermit)
      }

      Promise.resolve()
        .then(() =>
          creationStore.validateClosuresForCurrentForPicker(applySelection),
        )
        .then(ok => {
          if (ok) {
            applySelection()
          }
        })
    }, [creationStore, parentStore])

    if (!creationStore) {
      return null
    }

    return (
      <div className="workflow-directory col overflow-hidden">
        <SitePermitCreationFormContent
          store={creationStore}
          FileInputType={FileInputBase}
          isReadonly={false}
          isPreviewMode={isPreviewMode}
          allowedTypeIds={allowedTypeIds}
        />
        <div className="tw:flex tw:items-center tw:justify-between tw:p-2.5 tw:border-0 tw:border-t tw:border-solid tw:border-border">
          {allowSelectExisting ? (
            <Button variant="outline" onClick={onBackToList} size="lg">
              <ChecklistIcon data-icon="inline-start" size="xs" />
              {Localization.translator.select}
            </Button>
          ) : (
            <div />
          )}
          <div className="tw:flex tw:items-center tw:gap-2">
            <Button
              variant="outline"
              onClick={onCancel ?? onBackToList}
              size="lg"
            >
              {Localization.translator.cancel}
            </Button>
            <Button
              variant="default"
              onClick={handleApplyNewWorkflow}
              disabled={!creationStore.canSaveWorkflow}
              size="lg"
            >
              {Localization.translator.create}
            </Button>
          </div>
        </div>
      </div>
    )
  },
)

export default inject(
  'eventsStore',
  'workflowTypesStore',
  'companiesStore',
  'userProjectsStore',
  'projectDateStore',
  'fileUploadingStore',
  'permitStatusChangesStore',
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'projectMembersStore',
  'locationAttributesStore',
  'materialsStore',
  'materialCategoryStore',
  'tagsStore',
  'recurringWorkflowSettingsStore',
  'graphExecutorStore',
  'sitemapItemsStore',
  'closuresStore',
  'markupsStore',
  'calendarPlaceholderStore',
)(WorkflowCreationPanel)
